import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoading } from './loading_context'
import { useSnack } from './snack_context'
import {
    CashierAlreadyInUseError,
    CreateCashierInterface,
    InvalidCashierError,
    LoginCashier,
    createCashier,
    getCashierById,
    listCashiers,
    updateCashier,
} from '../services/cashier'
import { useAccount } from './account_context'
import { CoinInfo } from './coin_context'
import { PaymentMethodInfo } from './payment_method_context'
import { SystemBranchEnum, useConfigs } from './configs_context'
import { BranchInfo } from './branch_context'
import GenericModal from '../components/GenericModal'
import { StringField } from '../components/StringField'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { ClientInfo } from './client_context'
import { SupplierInfo } from './supplier_context'
import { useTranslation } from 'react-i18next'
import { isLogged } from '../services/auth'

export interface CashierInfo {
    id: number
    name: string
    hash: string
    isActive: boolean
    branch: BranchInfo | null
    automaticCashierOpenTime?: string | null
    automaticCashierCloseTime?: string | null
    closingCashier?: CashierInfo | null
}

export interface CraeteCashierInfo extends Omit<CashierInfo, 'id'> {
    id?: number
    isEditing: boolean
}

export interface CashierError {
    name: string
    hash: string
    branch: string
}

export enum CashierHistoricStatus {
    Open = 0,
    Closed = 1,
}

export interface CashierValue {
    id?: number
    coin?: CoinInfo
    type?: number
    value: number
    description?: string
    client?: ClientInfo | null
    supplier?: SupplierInfo | null
}

export enum CashierValueEnum {
    Initial = 0,
    Final = 1,
    In = 2,
    Out = 3,
}

export const CashierValueLabel: Record<number, string> = {
    0: 'Dinheiro inicial',
    1: 'Dinheiro final',
    2: 'Entrada',
    3: 'Saída',
}

export interface CashierHistoric {
    id: number
    cashierId: number
    status: number
    openingDate: string
    openingHour: string
    closingDate: string
    closingHour: string
    isSummerTime: boolean
    closingIsSummerTime: boolean
    cashier: CashierInfo
    values: CashierValue[]
}

export interface GeneralCashierExpectedAmountInfo {
    id: number
    coin?: CoinInfo
    paymentMethod?: PaymentMethodInfo
    initialValue: number
    finalValue: number
    amountIn: number
    amountOut: number
    expectedAmount: number
}

export interface DetailsCashierExpectedAmountInfo {
    type:
        | 'initialCash'
        | 'finalCash'
        | 'paymentSale'
        | 'paymentSaleChange'
        | 'paymentPurchase'
        | 'paymentPurchaseChange'
        | 'paymentPurchaseCost'
        | 'paymentPurchaseCostChange'
        | 'spent'
        | 'employee'
        | 'cashierIn'
        | 'cashierOut'
    direction: 'input' | 'output'
    amount: number
    date: string
    time: string
    isSummerTime: boolean
    referencedId: number
    coinId: number
    paymentMethod?: PaymentMethodInfo
    referencedPerson?: string
    referenceParentId?: number
    coin?: CoinInfo
    user?: string
    description?: string | null
    id?: number
    nome_caixa: string
    metodo_pagamento: string
}

export const CashierDetailsLabel: Record<string, string> = {
    initialCash: 'Dinero inicial',
    finalCash: 'Dinero final',
    paymentSale: 'Pagamiento venta',
    paymentSaleChange: 'Troco venta',
    paymentPurchase: 'Pagamiento compra',
    paymentPurchaseChange: 'Troco compra',
    paymentPurchaseCost: 'Pagamiento costos',
    paymentPurchaseCostChange: 'Troco costos',
    spent: 'Gasto',
    employee: 'Funcionário',
    cashierIn: 'Entrada caja',
    cashierOut: 'Salída caja',
}

export interface CashierExpectedAmountInfo {
    general: GeneralCashierExpectedAmountInfo[]
    datails: DetailsCashierExpectedAmountInfo[]
    finalBalance: number
    count: number
}

const cashierInfoDefault: CraeteCashierInfo = {
    name: '',
    hash: '',
    isActive: true,
    isEditing: false,
    branch: null,
    automaticCashierOpenTime: null,
    automaticCashierCloseTime: null,
    closingCashier: null,
}

const cashierErrorDefault: CashierError = {
    name: '',
    hash: '',
    branch: '',
}

export interface CashierContextInterface {
    loggedCashier: CashierInfo | null
    setLoggedCashier: Dispatch<SetStateAction<CashierInfo | null>>
    cashiers: CashierInfo[]
    setCashiers: Dispatch<SetStateAction<CashierInfo[]>>
    createCashierInfo: CraeteCashierInfo
    setCreateCashierInfo: Dispatch<SetStateAction<CraeteCashierInfo>>
    cashierError: CashierError
    setCashierError: Dispatch<SetStateAction<CashierError>>
    validate(): boolean
    handleCreateNewCashier(): Promise<void>
    startNewCashier(): void
    editCashier(id: number): Promise<void>
    setOpenCashierHashModal: Dispatch<SetStateAction<boolean>>
    handleChangeCashierFromList(branchId?: number, cashierId?: number): void
}

const cashierContextDefault: CashierContextInterface = {
    loggedCashier: null,
    setLoggedCashier: () => {},
    cashiers: [],
    setCashiers: () => {},
    createCashierInfo: cashierInfoDefault,
    setCreateCashierInfo: () => {},
    cashierError: cashierErrorDefault,
    setCashierError: () => {},
    validate: () => false,
    handleCreateNewCashier: () => Promise.resolve(),
    startNewCashier: () => {},
    editCashier: () => Promise.resolve(),
    setOpenCashierHashModal: () => false,
    handleChangeCashierFromList: () => {},
}

const CashierContext = createContext<CashierContextInterface>(
    cashierContextDefault
)

export function useCashier() {
    return useContext(CashierContext)
}

interface Props {
    children: ReactNode
}

export function CashierContextProvider({ children }: Props) {
    const { t } = useTranslation()
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const [createCashierInfo, setCreateCashierInfo] =
        useState<CraeteCashierInfo>(cashierInfoDefault)
    const [cashierError, setCashierError] =
        useState<CashierError>(cashierErrorDefault)
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const [cashiers, setCashiers] = useState<CashierInfo[]>([])
    const [loggedCashier, setLoggedCashier] = useState<CashierInfo | null>(null)
    const [newCashierHash, setNewCashierHash] = useState('')
    const [openCashierHashModal, setOpenCashierHashModal] = useState(false)
    const newCashierRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (!openCashierHashModal) return

        setTimeout(() => {
            newCashierRef.current?.focus()
        }, 100)
    }, [openCashierHashModal])

    useEffect(() => {
        if (!accountInfo || !configs.id || loggedCashier) return

        manageHash()
    }, [accountInfo, configs, loggedCashier])

    const manageHash = useCallback(async () => {
        const localStorageHash = localStorage.getItem('cashierHash')

        const cashierHash = newCashierHash || localStorageHash

        try {
            if (!cashierHash) throw new Error('Cashier hash not found')

            const res = await LoginCashier({ hash_caixa: cashierHash })
            setLoggedCashier(res)
            localStorage.setItem('cashierHash', cashierHash)
            if (!localStorageHash) window.location.reload()
            setNewCashierHash('')
        } catch (error) {
            if (error instanceof InvalidCashierError) {
                snack.error('Caja no encontrado!')

                if (cashierHash === localStorageHash) {
                    localStorage.removeItem('cashierHash')
                }
            }

            setOpenCashierHashModal(true)
            setNewCashierHash('')
        }
    }, [newCashierHash, snack])

    const validate = useCallback((): boolean => {
        let isOk = true
        const newCashierError = { ...cashierErrorDefault }

        if (!createCashierInfo.name) {
            newCashierError.name = 'Campo obrigatório'
            isOk = false
        }
        if (!createCashierInfo.hash) {
            newCashierError.hash = 'Campo obrigatório'
            isOk = false
        }
        if (
            accountInfo?.isMaster &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !createCashierInfo.branch
        ) {
            isOk = false
            newCashierError.branch = 'Campo obrigatório'
        }

        setCashierError({ ...newCashierError })
        return isOk
    }, [createCashierInfo, accountInfo, configs])

    const handleCreateNewCashier = useCallback(async () => {
        loading.show()
        try {
            const body: CreateCashierInterface = {
                nome: createCashierInfo.name,
                hash: createCashierInfo.hash,
                flag_ativo: Number(createCashierInfo.isActive),
                id_filial_master: createCashierInfo.branch?.id,
                hora_abertura_automatico:
                    createCashierInfo.automaticCashierOpenTime || null,
                hora_fechamento_automatico:
                    createCashierInfo.automaticCashierCloseTime || null,
                id_caixa_fechamento:
                    createCashierInfo.closingCashier?.id || null,
            }

            if (!createCashierInfo.isEditing) await createCashier(body)
            else
                await updateCashier({
                    ...body,
                    id_caixa: createCashierInfo.id ?? 0,
                })
            snack.success(
                `Caixa ${
                    !createCashierInfo.isEditing ? 'criado' : 'atualizado'
                } com sucesso!`
            )
            navigate('/system/cashier')
            setCreateCashierInfo({ ...cashierInfoDefault })
            setCashierError({ ...cashierErrorDefault })
        } catch (error) {
            if (error instanceof CashierAlreadyInUseError)
                setCashierError((prev) => ({
                    ...prev,
                    hash: 'Código já está em uso',
                }))
            else snack.connectionFail(error)
        }
        loading.hide()
    }, [createCashierInfo, loading, snack, navigate])

    const startNewCashier = useCallback(() => {
        setCreateCashierInfo({ ...cashierInfoDefault })
        setCashierError({ ...cashierErrorDefault })
    }, [])

    const editCashier = useCallback(
        async (id: number) => {
            try {
                const cashier = await getCashierById(id)

                setCreateCashierInfo((prev) => ({
                    ...cashier,
                    isEditing: true,
                }))
                setCashierError({ ...cashierErrorDefault })
            } catch (error) {
                console.log(error)
                snack.error('Caixa não encontrado')
                throw new Error('Cashier not found')
            }
        },
        [snack]
    )

    const handleChangeCashierFromList = useCallback(
        async (branchId?: number, cashierId?: number) => {
            if (!loggedCashier || !cashiers.length) return

            if (cashierId) {
                if (loggedCashier.id === cashierId) return
            } else if (branchId) {
                if (loggedCashier.branch?.id === branchId) return
            } else {
                return
            }

            const cashier = cashiers.find((c) => {
                if (cashierId) return c.id === cashierId
                else if (branchId) return c.branch?.id === branchId
                return false
            })

            if (!cashier) return

            setLoggedCashier(cashier)
            localStorage.setItem('cashierHash', cashier.hash)
            snack.info(`Caja trocado para: ${cashier.name}`, 6000)
        },
        [loggedCashier, cashiers, snack]
    )

    return (
        <>
            <GenericModal
                open={openCashierHashModal}
                handleClose={() => {
                    if (loggedCashier) setOpenCashierHashModal(false)
                }}
                textButton="Confirmar"
                title="Seleccione el caja"
                handleSubmit={() => {
                    setOpenCashierHashModal(false)
                    manageHash()
                }}
                disabledButton={!newCashierHash}
            >
                {
                    /* !configs.chooseCashier ||  */ !cashiers.length ? (
                        <StringField
                            label="Caja"
                            value={newCashierHash}
                            setValue={setNewCashierHash}
                            sx={{ width: '100%', mt: '5px' }}
                            ref={newCashierRef}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setOpenCashierHashModal(false)
                                    manageHash()
                                }
                            }}
                        />
                    ) : (
                        <FormControl
                            sx={{
                                width: '100%',
                                mt: '5px',
                            }}
                            size="medium"
                        >
                            <InputLabel>{t('Caja')}</InputLabel>
                            <Select
                                label={t('Caja')}
                                size="medium"
                                fullWidth
                                value={newCashierHash}
                                onChange={(e) => {
                                    const { value } = e.target
                                    if (!value) return
                                    setNewCashierHash(value)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setOpenCashierHashModal(false)
                                        manageHash()
                                    }
                                }}
                            >
                                {cashiers.map((cashier) => (
                                    <MenuItem value={cashier.hash}>
                                        {cashier.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )
                }
            </GenericModal>
            <CashierContext.Provider
                value={useMemo(
                    () => ({
                        loggedCashier,
                        setLoggedCashier,
                        cashiers,
                        setCashiers,
                        createCashierInfo,
                        setCreateCashierInfo,
                        cashierError,
                        setCashierError,
                        validate,
                        handleCreateNewCashier,
                        startNewCashier,
                        editCashier,
                        setOpenCashierHashModal,
                        handleChangeCashierFromList,
                    }),
                    [
                        loggedCashier,
                        setLoggedCashier,
                        cashiers,
                        setCashiers,
                        createCashierInfo,
                        setCreateCashierInfo,
                        cashierError,
                        setCashierError,
                        validate,
                        handleCreateNewCashier,
                        startNewCashier,
                        editCashier,
                        setOpenCashierHashModal,
                        handleChangeCashierFromList,
                    ]
                )}
            >
                {children}
            </CashierContext.Provider>
        </>
    )
}
